import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { apiClient } from '@/api/axiosClient';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { ClientDto, CustomerDto, MaterialCategoryDto, SupplierDto, UnitOfMeasureDto } from '@/types/materials';
import type { MaterialDto } from '@/types/materials';
import type { LabelTemplate } from '@/types/labelTemplate';
import type { QcTemplateDto } from '@/types/quality';
import type { LocationDto, WarehouseDto } from '@/types/organization';
import type { HardwareStationDto } from '@/types/weightProduction';

function buildSchema(t: (key: string) => string, tplEnabled: boolean) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    categoryId: z.string().nullable(),
    uomId: z.string().min(1),
    barcode: z.string().nullable(),
    abcClass: z.string().nullable(),
    supplierId: z.string().nullable(),
    // Required (see the .superRefine below) only once ThirdPartyLogisticsEnabled is on for this
    // company — every material's stock must be attributed to a Client (goods owner) and Customer
    // (dispatched to) under 3PL, mirroring GRN/Dispatch's own required-when-3PL fields.
    clientId: z.string().nullable(),
    customerId: z.string().nullable(),
    storageType: z.enum(['Pallet', 'Box', 'Loose']),
    rackNo: z.string().nullable(),
    binLocation: z.string().nullable(),
    // 3PL storage-handling requirement — purely informational today.
    storageConditionRequirement: z.string().nullable(),
    // The item's assigned putaway location — defaults every Putaway allocation row to this
    // Location when set (see PutawayService.SuggestLocationAsync).
    defaultLocationId: z.string().nullable(),
    batchTrackingRequired: z.boolean(),
    shelfLifeRequired: z.boolean(),
    shelfLifeDays: z.coerce.number().nullable(),
    qcRequired: z.boolean(),
    checkpointValidationRequired: z.boolean(),
    // Only meaningful while checkpointValidationRequired is true — forced to null server-side
    // otherwise (see MaterialService), so no client-side validation error is needed here.
    qcTemplateId: z.string().nullable(),
    minStock: z.coerce.number().min(0),
    maxStock: z.coerce.number().min(0),
    reorderLevel: z.coerce.number().min(0),
    safetyStock: z.coerce.number().min(0),
    unitWeight: z.coerce.number().nullable(),
    unitVolume: z.coerce.number().nullable(),
    stuffingQty: z.coerce.number().nullable(),
    status: z.enum(['Active', 'Inactive']),
    // UI-only — Material has no column for this. It's persisted by flipping LabelTemplate.MaterialId
    // on the chosen (and previously chosen, if different) template — see MaterialMaster.tsx's
    // syncLabelTemplateLink. Kept out of Create/UpdateMaterialRequest via passthroughFields.
    labelTemplateId: z.string().nullable(),
    // Weight-Based Production configuration — only meaningful/enforced (see .superRefine below)
    // while weightBasedProductionEnabled is true, mirroring checkpointValidationRequired's
    // "sub-fields only required while the parent switch is on" pattern above.
    weightBasedProductionEnabled: z.boolean(),
    standardWeight: z.coerce.number().nullable(),
    minWeight: z.coerce.number().nullable(),
    maxWeight: z.coerce.number().nullable(),
    weightUom: z.string().nullable(),
    toleranceType: z.enum(['Fixed', 'Percentage']).nullable(),
    toleranceValue: z.coerce.number().nullable(),
    stabilityDurationSeconds: z.coerce.number().min(1),
    autoGenerateBarcode: z.boolean(),
    autoPrintLabel: z.boolean(),
    weightProductionLabelTemplateId: z.string().nullable(),
    defaultHardwareStationId: z.string().nullable(),
  }).superRefine((data, ctx) => {
    if (tplEnabled) {
      if (!data.clientId) ctx.addIssue({ code: 'custom', path: ['clientId'], message: t('form.validation.clientRequired') });
      if (!data.customerId) ctx.addIssue({ code: 'custom', path: ['customerId'], message: t('form.validation.customerRequired') });
    }
    if (data.weightBasedProductionEnabled) {
      if (data.minWeight === null) ctx.addIssue({ code: 'custom', path: ['minWeight'], message: t('form.validation.minWeightRequired') });
      if (data.maxWeight === null) ctx.addIssue({ code: 'custom', path: ['maxWeight'], message: t('form.validation.maxWeightRequired') });
      if (data.minWeight !== null && data.maxWeight !== null && data.minWeight > data.maxWeight) {
        ctx.addIssue({ code: 'custom', path: ['maxWeight'], message: t('form.validation.maxWeightBelowMin') });
      }
      if (!data.weightUom) ctx.addIssue({ code: 'custom', path: ['weightUom'], message: t('form.validation.weightUomRequired') });
      // The Kiosk auto-calculates every label's expiry from today + this item's shelf life — with
      // nothing to calculate from, Weight-Based Production can't be turned on.
      if (!data.shelfLifeRequired) ctx.addIssue({ code: 'custom', path: ['shelfLifeRequired'], message: t('form.validation.shelfLifeRequiredForWeightProduction') });
      if (!data.shelfLifeDays || data.shelfLifeDays <= 0) ctx.addIssue({ code: 'custom', path: ['shelfLifeDays'], message: t('form.validation.shelfLifeDaysRequiredForWeightProduction') });
    }
  });
}

export type MaterialFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface MaterialFormDialogProps {
  open: boolean;
  material: MaterialDto | null;
  onClose: () => void;
  onSubmit: (values: MaterialFormValues) => void;
  loading?: boolean;
}

const DEFAULTS: MaterialFormValues = {
  code: '', name: '', categoryId: null, uomId: '', barcode: null, abcClass: null, supplierId: null, clientId: null, customerId: null,
  storageType: 'Box', rackNo: null, binLocation: null, storageConditionRequirement: null, defaultLocationId: null, batchTrackingRequired: false, shelfLifeRequired: false,
  shelfLifeDays: null, qcRequired: false, checkpointValidationRequired: false, qcTemplateId: null, minStock: 0, maxStock: 0, reorderLevel: 0, safetyStock: 0,
  unitWeight: null, unitVolume: null, stuffingQty: null, status: 'Active',
  labelTemplateId: null,
  weightBasedProductionEnabled: false, standardWeight: null, minWeight: null, maxWeight: null, weightUom: 'KG',
  toleranceType: 'Fixed', toleranceValue: null, stabilityDurationSeconds: 2, autoGenerateBarcode: true, autoPrintLabel: true,
  weightProductionLabelTemplateId: null, defaultHardwareStationId: null,
};

export default function MaterialFormDialog({ open, material, onClose, onSubmit, loading }: MaterialFormDialogProps) {
  const { t } = useTranslation('materialMaster');
  const { data: config } = useProcessConfiguration();
  const thirdPartyLogisticsEnabled = config?.thirdPartyLogisticsEnabled ?? false;
  const schema = buildSchema(t, thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: open && thirdPartyLogisticsEnabled,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await apiClient.get<CustomerDto[]>('/customers')).data,
    enabled: open,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['material-categories'],
    queryFn: async () => (await apiClient.get<MaterialCategoryDto[]>('/material-categories')).data,
    enabled: open,
  });
  const { data: uoms = [] } = useQuery({
    queryKey: ['units-of-measure'],
    queryFn: async () => (await apiClient.get<UnitOfMeasureDto[]>('/units-of-measure')).data,
    enabled: open,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
    enabled: open,
  });
  const { data: labelTemplates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
    enabled: open,
  });
  const { data: qcTemplates = [] } = useQuery({
    queryKey: ['qc-templates'],
    queryFn: async () => (await apiClient.get<QcTemplateDto[]>('/qc-templates')).data,
    enabled: open,
  });
  const weightProductionEnabled = config?.weightProductionEnabled ?? false;
  const { data: hardwareStations = [] } = useQuery({
    queryKey: ['hardware-stations'],
    queryFn: async () => (await apiClient.get<HardwareStationDto[]>('/hardware-stations')).data,
    enabled: open && weightProductionEnabled,
  });
  // WeightProduction-assigned templates (exclusive to this module) PLUS Generic ones — "Generic"
  // means usable anywhere in this build, which is why the GRN/Production/Job Work print pools accept
  // them too; excluding them here left this picker empty for any company that hadn't yet designed a
  // dedicated weight label, with no way to assign anything. Packing/GRN-specific layouts stay out.
  // Unlike the general labelTemplateId field above, this is a direct FK on Material (not the
  // MaterialId-on-template linking mechanism), so no per-material exclusivity filtering is needed.
  const weightProductionTemplates = labelTemplates.filter((tpl) => tpl.assignmentType === 'WeightProduction' || tpl.assignmentType === 'Generic');
  // GET /locations requires warehouseId (omitting it binds to Guid.Empty server-side and matches
  // nothing) — this picker isn't scoped to one warehouse (Material has no wired warehouse field
  // yet), so it fetches every warehouse's locations and flattens them into one list.
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
    enabled: open,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations', warehouses.map((w) => w.id).join(',')],
    queryFn: async () => {
      const responses = await Promise.all(warehouses.map((w) => apiClient.get<LocationDto[]>('/locations', { params: { warehouseId: w.id } })));
      return responses.flatMap((r) => r.data);
    },
    enabled: open && warehouses.length > 0,
  });
  // Only offer templates that aren't already claimed by a different item — the one already
  // linked to this material (if editing) is always included so it shows as the current selection.
  const selectableTemplates = labelTemplates.filter((tpl) => !tpl.materialId || tpl.materialId === material?.id);

  const { control, handleSubmit, reset, watch, setValue } = useForm<MaterialFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      setTab(0);
      reset(
        material
          ? {
              code: material.code, name: material.name, categoryId: material.categoryId, uomId: material.uomId,
              barcode: material.barcode, abcClass: material.abcClass, supplierId: material.supplierId,
              clientId: material.clientId, customerId: material.customerId,
              storageType: material.storageType, rackNo: material.rackNo, binLocation: material.binLocation,
              storageConditionRequirement: material.storageConditionRequirement,
              defaultLocationId: material.defaultLocationId,
              batchTrackingRequired: material.batchTrackingRequired, shelfLifeRequired: material.shelfLifeRequired,
              shelfLifeDays: material.shelfLifeDays, qcRequired: material.qcRequired,
              checkpointValidationRequired: material.checkpointValidationRequired,
              qcTemplateId: material.qcTemplateId,
              minStock: material.minStock, maxStock: material.maxStock, reorderLevel: material.reorderLevel,
              safetyStock: material.safetyStock, unitWeight: material.unitWeight, unitVolume: material.unitVolume,
              stuffingQty: material.stuffingQty,
              status: material.status as 'Active' | 'Inactive',
              labelTemplateId: null,
              weightBasedProductionEnabled: material.weightBasedProductionEnabled,
              standardWeight: material.standardWeight, minWeight: material.minWeight, maxWeight: material.maxWeight,
              weightUom: material.weightUom ?? 'KG', toleranceType: material.toleranceType,
              toleranceValue: material.toleranceValue, stabilityDurationSeconds: material.stabilityDurationSeconds,
              autoGenerateBarcode: material.autoGenerateBarcode, autoPrintLabel: material.autoPrintLabel,
              weightProductionLabelTemplateId: material.weightProductionLabelTemplateId,
              defaultHardwareStationId: material.defaultHardwareStationId,
            }
          : { ...DEFAULTS },
      );
    }
  }, [open, material, uoms, reset]);

  // Separate from the reset above: labelTemplates loads asynchronously (after the dialog is
  // already open), so it can't be baked into the initial reset without a race. Only touches this
  // one field, so it never clobbers edits already made elsewhere in the form while this resolves.
  useEffect(() => {
    if (!open || !material) return;
    const linked = labelTemplates.find((tpl) => tpl.materialId === material.id);
    setValue('labelTemplateId', linked?.id ?? null);
  }, [open, material, labelTemplates, setValue]);

  const shelfLifeRequired = watch('shelfLifeRequired');
  const qcRequired = watch('qcRequired');
  const checkpointValidationRequired = watch('checkpointValidationRequired');
  const watchedClientId = watch('clientId');
  const mappedCustomerIds = clients.find((c) => c.id === watchedClientId)?.customerIds ?? [];
  const weightBasedProductionEnabled = watch('weightBasedProductionEnabled');

  // Weight UOM is stored as the unit's code (e.g. "KG"), not its id, so the options are codes drawn
  // from the Units of Measure master. A value already saved on this material is kept in the list
  // even if that unit has since been removed or was never defined here — otherwise simply opening
  // an existing material and saving would silently blank a unit the user never touched.
  const watchedWeightUom = watch('weightUom');
  const weightUomOptions = useMemo(() => {
    const options = uoms
      .filter((u) => u.status === 'Active')
      .map((u) => ({ value: u.code, label: `${u.code} — ${u.name}` }));
    if (watchedWeightUom && !options.some((o) => o.value === watchedWeightUom)) {
      options.unshift({ value: watchedWeightUom, label: watchedWeightUom });
    }
    return options;
  }, [uoms, watchedWeightUom]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{material ? material.name : t('addMaterial')}</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('tabs.general')} />
        <Tab label={t('tabs.storage')} />
        <Tab label={t('tabs.policy')} />
        {weightProductionEnabled && <Tab label={t('weightProduction:materialTab.title')} />}
      </Tabs>
      <DialogContent>
        {tab === 0 && (
          <GridContainer spacing={2} columns={2}>
            <GridItem xs={1}>
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} label={t('form.code')} fullWidth sx={{ mt: 1 }} disabled={Boolean(material)} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} label={t('form.name')} fullWidth sx={{ mt: 1 }} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('form.category')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    options={[{ value: '', label: '—' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="uomId"
                control={control}
                render={({ field, fieldState }) => (
                  <SearchableSelect
                    label={t('form.uom')} value={field.value} onChange={field.onChange} fullWidth
                    helperText={fieldState.error?.message}
                    options={uoms.map((u) => ({ value: u.id, label: `${u.code} — ${u.name}` }))}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="barcode" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.barcode')} fullWidth />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="abcClass"
                control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} select label={t('form.abcClass')} fullWidth>
                    <MenuItem value="">{t('form.abcNone')}</MenuItem>
                    <MenuItem value="A">Category A</MenuItem>
                    <MenuItem value="B">Category B</MenuItem>
                    <MenuItem value="C">Category C</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="supplierId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('form.supplier')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    options={[{ value: '', label: '—' }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))]}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="labelTemplateId"
                control={control}
                render={({ field, fieldState }) => (
                  <SearchableSelect
                    label={t('form.labelTemplate')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    helperText={fieldState.error?.message ?? t('form.labelTemplateHelper')}
                    options={[
                      { value: '', label: t('form.labelTemplateNone') },
                      ...selectableTemplates.map((tpl) => ({ value: tpl.id, label: `${tpl.name}${tpl.status !== 'Active' ? ` (${tpl.status})` : ''}` })),
                    ]}
                  />
                )}
              />
            </GridItem>
            {thirdPartyLogisticsEnabled && (
              <>
                <GridItem xs={1}>
                  <Controller
                    name="clientId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <SearchableSelect
                        label={t('form.client')} value={field.value ?? ''} fullWidth
                        helperText={fieldState.error?.message}
                        onChange={(next) => {
                          field.onChange(next || null);
                          setValue('customerId', null);
                        }}
                        options={clients.map((c) => ({ value: c.id, label: c.name }))}
                      />
                    )}
                  />
                </GridItem>
                <GridItem xs={1}>
                  <Controller
                    name="customerId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <SearchableSelect
                        label={t('form.customer')} value={field.value ?? ''} onChange={(next) => field.onChange(next || null)} fullWidth
                        disabled={!watchedClientId}
                        helperText={fieldState.error?.message ?? (watchedClientId ? undefined : t('form.customerHelperNeedsClient'))}
                        options={customers.filter((c) => mappedCustomerIds.includes(c.id)).map((c) => ({ value: c.id, label: c.name }))}
                      />
                    )}
                  />
                </GridItem>
              </>
            )}
          </GridContainer>
        )}

        {tab === 1 && (
          <GridContainer spacing={2} columns={3}>
            <GridItem xs={1}>
              <Controller
                name="storageType"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label={t('form.storageType')} fullWidth sx={{ mt: 1 }}>
                    <MenuItem value="Pallet">Pallet</MenuItem>
                    <MenuItem value="Box">Box</MenuItem>
                    <MenuItem value="Loose">Loose</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="rackNo" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.rackNo')} fullWidth sx={{ mt: 1 }} />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="binLocation" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.binLocation')} fullWidth sx={{ mt: 1 }} />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="storageConditionRequirement"
                control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} select label={t('form.storageConditionRequirement')} fullWidth sx={{ mt: 1 }}>
                    <MenuItem value="">{t('form.storageConditionNone')}</MenuItem>
                    <MenuItem value="Ambient">{t('form.storageConditionAmbient')}</MenuItem>
                    <MenuItem value="Cold Storage">{t('form.storageConditionColdStorage')}</MenuItem>
                    <MenuItem value="Hazardous">{t('form.storageConditionHazardous')}</MenuItem>
                    <MenuItem value="Fragile">{t('form.storageConditionFragile')}</MenuItem>
                    <MenuItem value="Temperature Controlled">{t('form.storageConditionTemperatureControlled')}</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="defaultLocationId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('form.defaultLocation')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    helperText={t('form.defaultLocationHelper')}
                    options={[
                      { value: '', label: t('form.defaultLocationNone') },
                      ...locations.map((l) => ({ value: l.id, label: l.zoneName ? `${l.zoneName} / ${l.code}` : l.code })),
                    ]}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={3}>
              <Controller
                name="batchTrackingRequired"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label={t('form.batchTrackingRequired')} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="shelfLifeRequired"
                control={control}
                render={({ field, fieldState }) => (
                  <Stack>
                    <FormControlLabel
                      control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('form.shelfLifeRequired')}
                    />
                    {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
                  </Stack>
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="shelfLifeDays"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field} value={field.value ?? ''} type="number" label={t('form.shelfLifeDays')} fullWidth
                    disabled={!shelfLifeRequired} error={Boolean(fieldState.error)} helperText={fieldState.error?.message}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="qcRequired"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label={t('form.qcRequired')} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="checkpointValidationRequired"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} disabled={!qcRequired} onChange={(e) => field.onChange(e.target.checked)} />}
                    label={t('form.checkpointValidationRequired')}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="qcTemplateId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('form.qcTemplate')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    disabled={!checkpointValidationRequired}
                    helperText={t('form.qcTemplateHelper')}
                    options={[
                      { value: '', label: t('form.qcTemplateDefault') },
                      ...qcTemplates.map((tpl) => ({ value: tpl.id, label: `${tpl.name}${tpl.isDefault ? ` (${t('form.qcTemplateDefaultBadge')})` : ''}` })),
                    ]}
                  />
                )}
              />
            </GridItem>
          </GridContainer>
        )}

        {tab === 2 && (
          <GridContainer spacing={2} columns={2}>
            <GridItem xs={1}>
              <Controller name="minStock" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.minStock')} fullWidth sx={{ mt: 1 }} />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="maxStock" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.maxStock')} fullWidth sx={{ mt: 1 }} />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="reorderLevel" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.reorderLevel')} fullWidth />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="safetyStock" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.safetyStock')} fullWidth />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="unitWeight" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} type="number" label={t('form.unitWeight')} fullWidth />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller name="unitVolume" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} type="number" label={t('form.unitVolume')} fullWidth />} />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="stuffingQty"
                control={control}
                render={({ field }) => <TextField {...field} value={field.value ?? ''} type="number" label={t('form.stuffingQty')} helperText={t('form.stuffingQtyHelper')} fullWidth />}
              />
            </GridItem>
          </GridContainer>
        )}

        {tab === 3 && weightProductionEnabled && (
          <GridContainer spacing={2} columns={2}>
            <GridItem xs={2}>
              <Controller
                name="weightBasedProductionEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          // Printing a label per weighing IS the weight-based flow (the kiosk prints
                          // automatically the moment a weighing meets tolerance), so switching the
                          // feature on turns printing on with it — still overridable right below for
                          // the rare item that genuinely doesn't get a physical label.
                          if (e.target.checked) setValue('autoPrintLabel', true);
                        }}
                      />
                    }
                    label={t('weightProduction:materialTab.enabled')}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="standardWeight"
                control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label={t('weightProduction:materialTab.standardWeight')} fullWidth disabled={!weightBasedProductionEnabled} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="weightUom"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('weightProduction:materialTab.weightUom')}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    fullWidth
                    disabled={!weightBasedProductionEnabled}
                    options={weightUomOptions}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="minWeight"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field} value={field.value ?? ''} type="number" label={t('weightProduction:materialTab.minWeight')} fullWidth
                    disabled={!weightBasedProductionEnabled} error={Boolean(fieldState.error)} helperText={fieldState.error?.message}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="maxWeight"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field} value={field.value ?? ''} type="number" label={t('weightProduction:materialTab.maxWeight')} fullWidth
                    disabled={!weightBasedProductionEnabled} error={Boolean(fieldState.error)} helperText={fieldState.error?.message}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="toleranceType"
                control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? 'Fixed'} select label={t('weightProduction:materialTab.toleranceType')} fullWidth disabled={!weightBasedProductionEnabled}>
                    <MenuItem value="Fixed">{t('weightProduction:materialTab.toleranceTypeFixed')}</MenuItem>
                    <MenuItem value="Percentage">{t('weightProduction:materialTab.toleranceTypePercentage')}</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="toleranceValue"
                control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label={t('weightProduction:materialTab.toleranceValue')} fullWidth disabled={!weightBasedProductionEnabled} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="stabilityDurationSeconds"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label={t('weightProduction:materialTab.stabilityDurationSeconds')} fullWidth disabled={!weightBasedProductionEnabled} />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="autoGenerateBarcode"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} disabled={!weightBasedProductionEnabled} onChange={(e) => field.onChange(e.target.checked)} />}
                    label={t('weightProduction:materialTab.autoGenerateBarcode')}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="autoPrintLabel"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} disabled={!weightBasedProductionEnabled} onChange={(e) => field.onChange(e.target.checked)} />}
                    label={t('weightProduction:materialTab.autoPrintLabel')}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="weightProductionLabelTemplateId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('weightProduction:materialTab.labelTemplate')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    disabled={!weightBasedProductionEnabled}
                    helperText={t('weightProduction:materialTab.labelTemplateHelper')}
                    options={[
                      { value: '', label: t('form.labelTemplateNone') },
                      ...weightProductionTemplates.map((tpl) => ({ value: tpl.id, label: `${tpl.name}${tpl.status !== 'Active' ? ` (${tpl.status})` : ''}` })),
                    ]}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={1}>
              <Controller
                name="defaultHardwareStationId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('weightProduction:materialTab.defaultHardwareStation')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    disabled={!weightBasedProductionEnabled}
                    options={[
                      { value: '', label: '—' },
                      ...hardwareStations.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                  />
                )}
              />
            </GridItem>
          </GridContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveMaterial')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
